import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { DatePicker } from "antd";
import "react-datepicker/dist/react-datepicker.css";
import "./BookReport.css";
import { collection, getDocs, where, query } from "firebase/firestore";
import { db } from "../Firebase/FirebaseConnection";

const BookReport = () => {
  const [dateRange, setDateRange] = useState([null, null]);
  const [activeChart, setActiveChart] = useState("overallBooks");
  const [borrowedCount, setBorrowedCount] = useState(0);
  const [returnedCount, setReturnedCount] = useState(0);
  const [availableCount, setAvailableCount] = useState(0);
  const [showScrollTooltip, setShowScrollTooltip] = useState(false);
  const [borrowedView, setBorrowedView] = useState("day");
  const [totalCopies, setTotalCopies] = useState(0);

  const [borrowedFrequencyData, setBorrowedFrequencyData] = useState({
    series: [{ name: "Books Borrowed", data: [] }],
    options: {
      chart: { type: "bar", height: 350 },
      xaxis: { categories: [] },
      colors: ["#00BFFF"],
      dataLabels: { enabled: true },
      tooltip: { theme: "dark" },
    },
  });

  useEffect(() => {
    const reportContainer = document.querySelector(".report-container");

    const handleScroll = () => {
      if (reportContainer.scrollTop > 300) {
        setShowScrollTooltip(true);
      } else {
        setShowScrollTooltip(false);
      }
    };

    if (reportContainer) {
      reportContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (reportContainer) {
        reportContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const scrollToTop = () => {
    document
      .querySelector(".report-container")
      .scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDateChange = (dates) => {
    setDateRange(dates || [null, null]);
  };

  const isWithinDateRange = (date, range) => {
    if (!range || !range[0] || !range[1]) return true;

    const [start, end] = range;

    const targetTime = date.toDate
      ? date.toDate().getTime()
      : new Date(date).getTime();

    const startTime = start.valueOf
      ? start.valueOf()
      : new Date(start).getTime();
    const endTime = end.valueOf ? end.valueOf() : new Date(end).getTime();

    return targetTime >= startTime && targetTime <= endTime;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const borrowedQuery =
          dateRange[0] && dateRange[1]
            ? query(
                collection(db, "issuedBooks"),
                where("borrowedDate", ">=", dateRange[0].toDate()),
                where("borrowedDate", "<=", dateRange[1].toDate())
              )
            : collection(db, "issuedBooks");

        const borrowedSnapshot = await getDocs(borrowedQuery);
        const borrowedData = borrowedSnapshot.docs.map((doc) => doc.data());

        const returnedQuery =
          dateRange[0] && dateRange[1]
            ? query(
                collection(db, "returnedBooks"),
                where("returnedDate", ">=", dateRange[0].toDate()),
                where("returnedDate", "<=", dateRange[1].toDate())
              )
            : collection(db, "returnedBooks");

        const returnedSnapshot = await getDocs(returnedQuery);
        const returnedData = returnedSnapshot.docs.map((doc) => doc.data());

        const booksSnapshot = await getDocs(collection(db, "books"));
        const booksData = booksSnapshot.docs.map((doc) => doc.data());

        const filteredBooks =
          dateRange[0] && dateRange[1]
            ? booksData.filter((book) => {
                if (book.addedDate) {
                  const addedDate = book.addedDate.toDate
                    ? book.addedDate.toDate()
                    : new Date(book.addedDate);

                  return addedDate >= dateRange[0] && addedDate <= dateRange[1];
                }
                return false;
              })
            : booksData;

        const totalAvailable = filteredBooks.reduce((sum, book) => {
          const availableCopies = parseInt(book.copiesAvailable, 10) || 0;
          return sum + availableCopies;
        }, 0);

        const totalAllCopies = filteredBooks.reduce((sum, book) => {
          const total = parseInt(book.totalCopies, 10) || 0;
          return sum + total;
        }, 0);

        setTotalCopies(totalAllCopies);

        const activeStatuses = ["borrowed", "good", "damaged", "missing"];

        const totalBorrowedCopies = borrowedData.reduce((sum, item) => {
          const status = (item.status || "").toLowerCase();
          if (activeStatuses.includes(status)) {
            return sum + (parseInt(item.copiesBorrowed) || 0);
          }
          return sum;
        }, 0);

        const totalReturnedCopies = returnedData.reduce((sum, item) => {
          return sum + (parseInt(item.copiesReturned) || 0);
        }, 0);

        setBorrowedCount(totalBorrowedCopies);
        setReturnedCount(totalReturnedCopies);

        setAvailableCount(totalAvailable);
      } catch (error) {}
    };

    fetchData();
  }, [dateRange]);

  useEffect(() => {
    const fetchBorrowedFrequency = async () => {
      try {
        const borrowedSnapshot = await getDocs(collection(db, "issuedBooks"));

        let categories = [];
        let data = [];

        if (borrowedView === "day") {
          const dayCounts = Array(7).fill(0);
          borrowedSnapshot.docs.forEach((doc) => {
            const borrowedDate = doc.data().borrowedDate?.toDate();
            if (borrowedDate && isWithinDateRange(borrowedDate, dateRange)) {
              const day = borrowedDate.getDay();
              dayCounts[day]++;
            }
          });
          categories = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
          data = dayCounts;
        } else {
          const monthCounts = Array(12).fill(0);
          borrowedSnapshot.docs.forEach((doc) => {
            const borrowedDate = doc.data().borrowedDate?.toDate();
            if (borrowedDate && isWithinDateRange(borrowedDate, dateRange)) {
              const month = borrowedDate.getMonth();
              const copies = parseInt(doc.data().copiesBorrowed) || 0;
              monthCounts[month] += copies;
            }
          });
          categories = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ];
          data = monthCounts;
        }

        setBorrowedFrequencyData((prev) => ({
          ...prev,
          series: [{ name: "Books Borrowed", data }],
          options: {
            ...prev.options,
            xaxis: { categories },
          },
        }));
      } catch (error) {
        console.error("Error fetching borrowed frequency:", error);
      }
    };

    fetchBorrowedFrequency();
  }, [borrowedView, dateRange]);

  useEffect(() => {
    const fetchTopReaders = async () => {
      try {
        const snapshot = await getDocs(collection(db, "reading_sessions"));
        const readerCounts = {};

        snapshot.docs.forEach((doc) => {
          const data = doc.data();

          const readTimestamp = data.finishedTimestamp?.toDate
            ? data.finishedTimestamp.toDate()
            : new Date(data.finishedTimestamp);

          if (
            data.status === "Done Reading" &&
            isWithinDateRange(readTimestamp, dateRange)
          ) {
            const name = data.fullName || "Unknown";
            readerCounts[name] = (readerCounts[name] || 0) + 1;
          }
        });

        const sortedReaders = Object.entries(readerCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10);

        setTopReadersData((prevState) => ({
          ...prevState,
          series: [
            {
              name: "Books Read",
              data: sortedReaders.map(([, count]) => count),
            },
          ],
          options: {
            ...prevState.options,
            xaxis: { categories: sortedReaders.map(([name]) => name) },
          },
        }));
      } catch (error) {
        console.error("Error fetching top readers:", error);
      }
    };

    fetchTopReaders();
  }, [dateRange]);

  useEffect(() => {
    const fetchTopReadBooks = async () => {
      try {
        const snapshot = await getDocs(collection(db, "reading_sessions"));
        const bookCounts = {};

        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          const readDate = data.finishedTimestamp?.toDate
            ? data.finishedTimestamp.toDate()
            : new Date(data.finishedTimestamp);

          if (
            data.status === "Done Reading" &&
            isWithinDateRange(readDate, dateRange)
          ) {
            const title = data.bookTitle || "Untitled";
            bookCounts[title] = (bookCounts[title] || 0) + 1;
          }
        });

        const sortedBooks = Object.entries(bookCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10);

        setTopReadBooksData((prevState) => ({
          ...prevState,
          series: [
            { name: "Read Count", data: sortedBooks.map(([, count]) => count) },
          ],
          options: {
            ...prevState.options,
            xaxis: { categories: sortedBooks.map(([title]) => title) },
          },
        }));
      } catch (error) {
        console.error("Error fetching top read books:", error);
      }
    };

    fetchTopReadBooks();
  }, [dateRange]);

  useEffect(() => {
    const fetchBooksAdded = async () => {
      try {
        const booksSnapshot = await getDocs(collection(db, "books"));
        const booksData = booksSnapshot.docs.map((doc) => doc.data());

        const monthlyData = Array(12).fill(0);

        booksData.forEach((book) => {
          if (book.addedDate && isWithinDateRange(book.addedDate, dateRange)) {
            const addedDate = new Date(book.addedDate);
            const month = addedDate.getMonth();
            monthlyData[month] += 1;
          }
        });

        setAddedBooksData((prevState) => ({
          ...prevState,
          series: [{ ...prevState.series[0], data: monthlyData }],
        }));
      } catch (error) {}
    };

    fetchBooksAdded();
  }, [dateRange]);

  useEffect(() => {
    const fetchMostBorrowedBooks = async () => {
      try {
        const borrowedQuery =
          dateRange[0] && dateRange[1]
            ? query(
                collection(db, "issuedBooks"),
                where("borrowedDate", ">=", dateRange[0].toDate()),
                where("borrowedDate", "<=", dateRange[1].toDate())
              )
            : collection(db, "issuedBooks");

        const borrowedSnapshot = await getDocs(borrowedQuery);
        const borrowedData = borrowedSnapshot.docs.map((doc) => doc.data());

        const borrowCounts = {};
        borrowedData.forEach((book) => {
          const title = book.title || "Unknown";
          borrowCounts[title] = (borrowCounts[title] || 0) + 1;
        });

        const sortedBooks = Object.entries(borrowCounts)
          .map(([title, count]) => ({ title, count }))
          .sort((a, b) => b.count - a.count);

        const top10Books = sortedBooks.slice(0, 10);

        setMostBorrowedBooksData({
          ...mostBorrowedBooksData,
          series: [
            {
              name: "Borrowed Count",
              data: top10Books.map((book) => book.count),
            },
          ],
          options: {
            ...mostBorrowedBooksData.options,
            xaxis: {
              ...mostBorrowedBooksData.options.xaxis,
              categories: top10Books.map((book) => book.title),
            },
          },
        });
      } catch (error) {}
    };

    fetchMostBorrowedBooks();
  }, [dateRange]);

  useEffect(() => {
    const fetchReturnData = async () => {
      try {
        let returnedQuery;

        if (dateRange[0] && dateRange[1]) {
          returnedQuery = query(
            collection(db, "returnedBooks"),
            where("returnedDate", ">=", dateRange[0].toDate()),
            where("returnedDate", "<=", dateRange[1].toDate())
          );
        } else {
          returnedQuery = collection(db, "returnedBooks");
        }

        const returnedSnapshot = await getDocs(returnedQuery);
        const returnedData = returnedSnapshot.docs.map((doc) => doc.data());

        const earlyReturns = {};
        const lateReturns = {};

        returnedData.forEach((entry) => {
          const department = entry.department || "Unknown";
          const dueDate = entry.dueDate.toDate();
          const returnedDate = entry.returnedDate.toDate();

          if (returnedDate < dueDate) {
            earlyReturns[department] = (earlyReturns[department] || 0) + 1;
          } else if (returnedDate > dueDate) {
            lateReturns[department] = (lateReturns[department] || 0) + 1;
          }
        });

        const earlyReturnSeries = Object.values(earlyReturns);
        const earlyReturnLabels = Object.keys(earlyReturns);

        const lateReturnSeries = Object.values(lateReturns);
        const lateReturnLabels = Object.keys(lateReturns);

        setEarlyReturnLevelsData({
          ...earlyReturnLevelsData,
          series: earlyReturnSeries,
          options: {
            ...earlyReturnLevelsData.options,
            labels: earlyReturnLabels,
          },
        });

        setLateReturnLevelsData({
          ...lateReturnLevelsData,
          series: lateReturnSeries,
          options: {
            ...lateReturnLevelsData.options,
            labels: lateReturnLabels,
          },
        });
      } catch (error) {}
    };

    fetchReturnData();
  }, [dateRange]);

  useEffect(() => {
    const fetchStatusData = async () => {
      try {
        const issuedSnapshot = await getDocs(collection(db, "issuedBooks"));
        const paidSnapshot = await getDocs(collection(db, "paidBooks"));
        const returnedSnapshot = await getDocs(collection(db, "returnedBooks"));
        const booksSnapshot = await getDocs(collection(db, "books"));

        const statusCounts = {
          Repaired: 0,
          Replaced: 0,
          Paid: 0,
          Damaged: 0,
          Missing: 0,
          Good: 0,
        };

        issuedSnapshot.docs.forEach((doc) => {
          const data = doc.data();

          if (
            data.borrowedDate &&
            isWithinDateRange(data.borrowedDate, dateRange)
          ) {
            const copies = parseInt(data.copiesBorrowed) || 0;

            if (data.status === "Resolved") {
              if (data.resolveType === "Repaired") {
                statusCounts.Repaired += copies;
              } else if (data.resolveType === "Replaced") {
                statusCounts.Replaced += copies;
              }
            } else if (data.status === "Damaged") {
              statusCounts.Damaged += copies;
            } else if (data.status === "Missing") {
              statusCounts.Missing += copies;
            } else if (data.status === "Good") {
              statusCounts.Good += copies;
            }
          }
        });

        paidSnapshot.docs.forEach((doc) => {
          const data = doc.data();

          if (
            data.datePaid &&
            isWithinDateRange(data.datePaid.toDate(), dateRange)
          ) {
            const copies = parseInt(data.copiesBorrowed) || 0;
            if (data.status && data.status.includes("Paid")) {
              statusCounts.Paid += copies;
            }
          }
        });

        returnedSnapshot.docs.forEach((doc) => {
          const data = doc.data();

          if (
            data.returnedDate &&
            isWithinDateRange(data.returnedDate.toDate(), dateRange)
          ) {
            if (data.status === "Good") {
              const copies = parseInt(data.copiesReturned) || 0;
              statusCounts.Good += copies;
            }
          }
        });

        booksSnapshot.docs.forEach((doc) => {
          const data = doc.data();

          const damaged =
            typeof data.damagedUnusableCount === "number"
              ? data.damagedUnusableCount
              : parseInt(data.damagedUnusableCopies || "0", 10);

          const missing =
            typeof data.missingCount === "number"
              ? data.missingCount
              : parseInt(data.missingCopies || "0", 10);

          const good = parseInt(data.copiesAvailable || "0", 10);

          if (data.bookSatus === "Good") {
            statusCounts.Good += good;
          }

          statusCounts.Damaged += damaged;
          statusCounts.Missing += missing;
        });

        setStatusData((prevState) => ({
          ...prevState,
          series: Object.values(statusCounts),
          options: {
            ...prevState.options,
            labels: Object.keys(statusCounts),
          },
        }));
      } catch (error) {
        console.error("Error fetching status data:", error);
      }
    };

    fetchStatusData();
  }, [dateRange]);

  const [lateReturnBooksData, setLateReturnBooksData] = useState({
    series: [],
    options: {
      chart: {
        type: "donut",
        height: 350,
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
        },
        background: "transparent",
      },
      stroke: {
        show: false,
      },
      labels: [],
      colors: ["#FF6B6B", "#FFB84C", "#FDD835", "#A5D6A7", "#64B5F6"],
      fill: {
        type: "gradient",
        gradient: {
          shade: "dark",
          type: "diagonal1",
          gradientToColors: [
            "#FF8A80",
            "#FFD54F",
            "#FBC02D",
            "#81C784",
            "#42A5F5",
          ],
          stops: [0, 50, 100],
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "65%",
            labels: {
              show: true,
              name: {
                fontSize: "16px",
                fontWeight: "bold",
                color: "#ddd",
                offsetY: -10,
              },
              value: {
                fontSize: "20px",
                fontWeight: "bold",
                color: "#fff",
                offsetY: 5,
                formatter: (val) => `${val}%`,
              },
              total: {
                show: true,
                label: "Total",
                color: "#fff",
                fontSize: "18px",
                fontWeight: "bold",
                formatter: (w) =>
                  `${w.globals.seriesTotals.reduce((a, b) => a + b, 0)}%`,
              },
            },
          },
        },
      },
      tooltip: {
        theme: "dark",
        y: {
          formatter: (val) => `${val} late returns 📚`,
        },
      },
      legend: {
        show: true,
        position: "bottom",
        fontSize: "14px",
        fontWeight: 600,
        labels: {
          colors: "#fff",
          useSeriesColors: true,
        },
        markers: {
          width: 14,
          height: 14,
          radius: 12,
        },
        itemMargin: {
          horizontal: 10,
          vertical: 5,
        },
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "14px",
          fontWeight: "bold",
          colors: ["#fff"],
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 2,
          opacity: 0.3,
        },
        formatter: (val, opts) =>
          `${opts.w.globals.labels[opts.seriesIndex]}: ${val.toFixed(1)}%`,
      },
    },
  });

  useEffect(() => {
    const fetchLateReturnBooks = async () => {
      try {
        const returnedSnapshot = await getDocs(collection(db, "returnedBooks"));
        const lateReturns = {};

        returnedSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          if (data.returnedDate && data.dueDate) {
            const returnedDate = data.returnedDate.toDate();
            const dueDate = data.dueDate.toDate();
            if (
              returnedDate > dueDate &&
              isWithinDateRange(returnedDate, dateRange)
            ) {
              const bookTitle = data.title || "Unknown";
              lateReturns[bookTitle] = (lateReturns[bookTitle] || 0) + 1;
            }
          }
        });

        const sortedBooks = Object.entries(lateReturns)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5);

        setLateReturnBooksData((prevState) => ({
          ...prevState,
          series: sortedBooks.map(([, count]) => count),
          options: {
            ...prevState.options,
            labels: sortedBooks.map(([title]) => title),
          },
        }));
      } catch (error) {
        console.error("Error fetching late return books data:", error);
      }
    };

    fetchLateReturnBooks();
  }, [dateRange]);

  const [borrowReturnTrendData, setBorrowReturnTrendData] = useState({
    series: [
      { name: "Borrowed Books", data: [] },
      { name: "Returned Books", data: [] },
    ],
    options: {
      chart: { type: "area", height: 350 },
      stroke: {
        curve: "smooth",
        width: 0.5,
      },

      grid: {
        borderColor: "rgba(255, 255, 255, 0.2)",
        strokeDashArray: 3,
      },
      xaxis: {
        categories: [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ],
      },
      colors: ["#FF5733", "#33FF57"],
    },
  });

  useEffect(() => {
    const fetchBorrowReturnTrend = async () => {
      try {
        const borrowedSnapshot = await getDocs(collection(db, "issuedBooks"));
        const returnedSnapshot = await getDocs(collection(db, "returnedBooks"));

        const monthlyBorrowed = Array(12).fill(0);
        const monthlyReturned = Array(12).fill(0);

        borrowedSnapshot.docs.forEach((doc) => {
          const borrowedTimestamp = doc.data().borrowedDate;
          if (borrowedTimestamp) {
            const borrowedDate = borrowedTimestamp.toDate();
            if (isWithinDateRange(borrowedDate, dateRange)) {
              const month = borrowedDate.getMonth();
              monthlyBorrowed[month] +=
                parseInt(doc.data().copiesBorrowed) || 0;
            }
          }
        });

        returnedSnapshot.docs.forEach((doc) => {
          const returnedTimestamp = doc.data().returnedDate;
          if (returnedTimestamp) {
            const returnedDate = returnedTimestamp.toDate();
            if (isWithinDateRange(returnedDate, dateRange)) {
              const month = returnedDate.getMonth();
              monthlyReturned[month]++;
            }
          }
        });

        setBorrowReturnTrendData((prevState) => ({
          ...prevState,
          series: [
            { name: "Borrowed Books", data: monthlyBorrowed },
            { name: "Returned Books", data: monthlyReturned },
          ],
        }));
      } catch (error) {
        console.error("Error fetching borrow return trend data:", error);
      }
    };

    fetchBorrowReturnTrend();
  }, [dateRange]);

  const [activeBorrowersData, setActiveBorrowersData] = useState({
    series: [{ name: "Books Borrowed", data: [] }],
    options: {
      chart: { type: "bar", height: 350 },
      plotOptions: {
        bar: {
          borderRadius: 6,
          columnWidth: "50%",
        },
      },

      grid: {
        borderColor: "rgba(255, 255, 255, 0.2)",
        strokeDashArray: 3,
      },
      xaxis: { categories: [] },
      colors: [
        "#FFADAD",
        "#FFD6A5",
        "#90BE6D",
        "#277DA1",
        "#F9844A",
        "#A0C4FF",
        "#E56B6F",
        "#264653",
        "#B56576",
        "#F9C74F",
      ],
    },
  });

  useEffect(() => {
    const fetchActiveBorrowers = async () => {
      try {
        const borrowedSnapshot = await getDocs(collection(db, "issuedBooks"));
        const userBorrowCounts = {};

        borrowedSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          const borrowedTimestamp = data.borrowedDate;
          if (borrowedTimestamp) {
            const borrowedDate = borrowedTimestamp.toDate();
            if (isWithinDateRange(borrowedDate, dateRange)) {
              const user = data.borrowedBy || "Unknown";
              const copies = parseInt(data.copiesBorrowed) || 0;
              userBorrowCounts[user] = (userBorrowCounts[user] || 0) + copies;
            }
          }
        });

        const sortedUsers = Object.entries(userBorrowCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10);

        setActiveBorrowersData({
          series: [
            {
              name: "Books Borrowed",
              data: sortedUsers.map(([, count]) => count),
            },
          ],
          options: {
            ...activeBorrowersData.options,
            xaxis: { categories: sortedUsers.map(([user]) => user) },
          },
        });
      } catch (error) {
        console.error("Error fetching active borrowers:", error);
      }
    };

    fetchActiveBorrowers();
  }, [dateRange]);

  const [peakHoursData, setPeakHoursData] = useState({
    series: [{ name: "Books Borrowed", data: [] }],
    options: {
      chart: { type: "bar", height: 350 },
      grid: {
        borderColor: "rgba(255, 255, 255, 0.2)",
        strokeDashArray: 3,
      },
      xaxis: {
        categories: Array.from({ length: 24 }, (_, i) => `${i}:00`),
        labels: {
          rotate: -45,
          style: {
            fontSize: "12px",
          },
        },
      },
      colors: ["#FF5733"],
    },
  });

  useEffect(() => {
    const fetchPeakHours = async () => {
      try {
        const borrowedSnapshot = await getDocs(collection(db, "issuedBooks"));
        const hourlyData = Array(24).fill(0);

        borrowedSnapshot.docs.forEach((doc) => {
          const borrowedTimestamp = doc.data().borrowedDate;
          if (borrowedTimestamp) {
            const borrowedDate = borrowedTimestamp.toDate();
            if (isWithinDateRange(borrowedDate, dateRange)) {
              const hour = borrowedDate.getHours();
              hourlyData[hour]++;
            }
          }
        });

        setPeakHoursData((prevState) => ({
          ...prevState,
          series: [{ name: "Books Borrowed", data: hourlyData }],
        }));
      } catch (error) {
        console.error("Error fetching peak hours data:", error);
      }
    };

    fetchPeakHours();
  }, [dateRange]);

  const [categoryPopularityData, setCategoryPopularityData] = useState({
    series: [],
    options: {
      chart: { type: "pie", height: 350 },
      labels: [],
      colors: ["#FF5733", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF"],
      stroke: {
        width: 0,
      },
      legend: {
        position: "bottom",
        fontSize: "14px",
        labels: {
          colors: "#333",
          useSeriesColors: false,
        },
        markers: {
          width: 12,
          height: 12,
          radius: 2,
        },
      },
    },
  });

  useEffect(() => {
    const fetchCategoryPopularity = async () => {
      try {
        const borrowedSnapshot = await getDocs(collection(db, "issuedBooks"));
        const categoryCounts = {};

        borrowedSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          const borrowedTimestamp = data.borrowedDate;
          if (borrowedTimestamp) {
            const borrowedDate = borrowedTimestamp.toDate();
            if (isWithinDateRange(borrowedDate, dateRange)) {
              const category = data.category || "Unknown";
              const copies = parseInt(data.copiesBorrowed) || 0;

              categoryCounts[category] =
                (categoryCounts[category] || 0) + copies;
            }
          }
        });

        const sortedCategories = Object.entries(categoryCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5);

        setCategoryPopularityData({
          series: sortedCategories.map(([, count]) => count),
          options: {
            ...categoryPopularityData.options,
            labels: sortedCategories.map(([category]) => category),
          },
        });
      } catch (error) {
        console.error("Error fetching category popularity data:", error);
      }
    };

    fetchCategoryPopularity();
  }, [dateRange]);

  const [borrowByYearLevelData, setBorrowByYearLevelData] = useState({
    series: [{ name: "Books Borrowed", data: [] }],
    options: {
      chart: { type: "bar", height: 350 },
      plotOptions: {
        bar: {
          borderRadius: 6,
          columnWidth: "50%",
        },
      },
      grid: {
        borderColor: "rgba(255, 255, 255, 0.2)",
        strokeDashArray: 3,
      },

      xaxis: { categories: ["1st Year", "2nd Year", "3rd Year", "4th Year"] },
      colors: ["#FFADAD"],
    },
  });

  useEffect(() => {
    const fetchBorrowByYearLevel = async () => {
      try {
        const borrowedSnapshot = await getDocs(collection(db, "issuedBooks"));
        const yearLevelCounts = {
          "1st Year": 0,
          "2nd Year": 0,
          "3rd Year": 0,
          "4th Year": 0,
        };

        const normalizeYearLevel = (level) => {
          const mapping = {
            "First Year": "1st Year",
            "Second Year": "2nd Year",
            "Third Year": "3rd Year",
            "Fourth Year": "4th Year",
          };
          return mapping[level] || level;
        };

        borrowedSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          const borrowedTimestamp = data.borrowedDate;

          if (borrowedTimestamp) {
            const borrowedDate = borrowedTimestamp.toDate();
            if (isWithinDateRange(borrowedDate, dateRange)) {
              const rawYearLevel = data.yearLevel || "Unknown";
              const yearLevel = normalizeYearLevel(rawYearLevel);
              const copies = parseInt(data.copiesBorrowed) || 0;
              if (yearLevelCounts.hasOwnProperty(yearLevel)) {
                yearLevelCounts[yearLevel] += copies;
              }
            }
          }
        });

        setBorrowByYearLevelData((prevState) => ({
          ...prevState,
          series: [
            { name: "Books Borrowed", data: Object.values(yearLevelCounts) },
          ],
        }));
      } catch (error) {
        console.error("Error fetching borrow by year level data:", error);
      }
    };

    fetchBorrowByYearLevel();
  }, [dateRange]);

  const [statusData, setStatusData] = useState({
    series: [],
    options: {
      chart: {
        type: "radialBar",
        toolbar: { show: false },
        background: "transparent",
      },
      plotOptions: {
        radialBar: {
          hollow: {
            size: "60%",
          },
          track: {
            background: "rgba(255, 255, 255, 0.15)",
            strokeWidth: "100%",
            margin: 5,
            opacity: 0.9,
          },
          dataLabels: {
            showOn: "always",
            name: {
              offsetY: -10,
              color: "#fff",
              fontSize: "18px",
              fontWeight: "bold",
            },
            value: {
              offsetY: 5,
              color: "#fff",
              fontSize: "22px",
              fontWeight: "bold",
              formatter: function (val) {
                return val + "%";
              },
            },
            total: {
              show: true,
              label: "Total",
              color: "#fff",
              fontSize: "20px",
              fontWeight: "bold",
              formatter: function (w) {
                return w.globals.seriesTotals.reduce((a, b) => a + b, 0) + "%";
              },
            },
          },
        },
      },
      labels: ["Repaired", "Replaced", "Paid", "Damaged", "Missing", "Good"],
      colors: [
        "#42A5F5",
        "#FF7043",
        "#4CAF50",
        "#F44336",
        "#FFC107",
        "#66BB6A",
      ],

      legend: {
        show: true,
        position: "bottom",
        fontSize: "14px",
        fontWeight: 600,
        labels: {
          colors: "#fff",
          useSeriesColors: true,
        },
        markers: {
          width: 14,
          height: 14,
          radius: 10,
        },
        itemMargin: {
          horizontal: 15,
          vertical: 8,
        },
      },
    },
  });

  const [addedBooksData, setAddedBooksData] = useState({
    series: [
      {
        name: "Books Added",
        data: [],
      },
    ],
    options: {
      chart: {
        type: "bar",
        height: 350,
        animations: { enabled: true },
      },
      grid: {
        show: true,
        borderColor: "rgba(255, 255, 255, 0.2)",
        strokeDashArray: 3,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "60%",
          borderRadius: 6,
        },
      },

      fill: {
        type: "gradient",
        gradient: {
          shade: "light",
          type: "horizontal",
          stops: [0, 100],
        },
      },
      xaxis: {
        categories: [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ],
      },
      colors: ["#FFCE56"],

      dataLabels: {
        enabled: false,
      },
    },
  });

  const overallBooksData = {
    series: [
      {
        name: "Books Count",
        data: [borrowedCount, availableCount, returnedCount, totalCopies],
      },
    ],
    options: {
      chart: {
        type: "bar",
        height: 350,
        animations: { enabled: true },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "50%",
          borderRadius: 8,
        },
      },
      colors: ["#FF5733", "#33FF57", "#3357FF"],

      xaxis: {
        categories: [
          "Issued Books",
          "Total Book Available",
          "Returned Book",
          "Book Total Copies",
        ],
        labels: {
          style: {
            fontSize: "14px",
            fontFamily: "Roboto",
          },
        },
      },
      grid: {
        borderColor: "#e7e7e7",
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "14px",
          colors: ["#444"],
        },
      },
    },
  };

  const [mostBorrowedBooksData, setMostBorrowedBooksData] = useState({
    series: [
      {
        name: "Borrowed Count",
        data: [],
      },
    ],
    options: {
      chart: {
        type: "bar",
        height: 400,
        animations: { enabled: true },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "50%",
          borderRadius: 6,
        },
      },
      colors: ["#36A2EB"],
      xaxis: {
        categories: [],
        labels: {
          style: {
            fontSize: "12px",
            fontFamily: "Roboto",
          },
        },
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "14px",
          fontFamily: "Roboto",
          colors: ["#2c3e50"],
        },
      },
      tooltip: {
        enabled: true,
        theme: "dark",
      },
    },
  });

  const [lateReturnLevelsData, setLateReturnLevelsData] = useState({
    series: [],
    options: {
      chart: {
        type: "pie",
        height: 350,
        animations: { enabled: true },
      },
      labels: [],
      colors: ["#FFADAD", "#FFD6A5", "#90BE6D", "#277DA1", "#F9844A"],
      stroke: {
        width: 0,
      },
      fill: {
        type: "gradient",
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${Math.round(val)}%`,
        style: {
          fontSize: "14px",
          fontFamily: "Roboto",
        },
      },
      legend: {
        position: "bottom",
      },
    },
  });

  const [earlyReturnLevelsData, setEarlyReturnLevelsData] = useState({
    series: [],
    options: {
      chart: {
        type: "pie",
        height: 350,
        animations: { enabled: true },
      },
      labels: ["Resolved"],
      colors: ["#28A745", "#17A2B8", "#FFC107", "#DC3545"],
      stroke: {
        width: 0,
      },
      fill: {
        type: "gradient",
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${Math.round(val)}%`,
        style: {
          fontSize: "14px",
          fontFamily: "Roboto",
        },
      },
      legend: {
        position: "bottom",
      },
    },
  });

  const [topReadersData, setTopReadersData] = useState({
    series: [{ name: "Books Read", data: [] }],
    options: {
      chart: { type: "bar", height: 350 },
      plotOptions: {
        bar: {
          borderRadius: 6,
          columnWidth: "50%",
        },
      },
      grid: {
        borderColor: "rgba(255, 255, 255, 0.2)",
        strokeDashArray: 3,
      },
      xaxis: { categories: [] },
      colors: ["#FFB347"],
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "14px",
          fontWeight: "bold",
          colors: ["#333"],
        },
      },
    },
  });

  const [topReadBooksData, setTopReadBooksData] = useState({
    series: [{ name: "Read Count", data: [] }],
    options: {
      chart: { type: "bar", height: 350 },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "60%",
          borderRadius: 6,
        },
      },
      xaxis: { categories: [] },
      colors: ["#6A5ACD"],
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "14px",
          fontWeight: "bold",
          colors: ["#fff"],
        },
      },
      tooltip: {
        theme: "dark",
        y: {
          formatter: (val) => `${val} reads`,
        },
      },
    },
  });

  const scrollToChart = (chartId) => {
    setActiveChart(chartId);

    setTimeout(() => {
      const chartElement = document.getElementById(chartId);
      if (chartElement) {
        chartElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  return (
    <>
      {showScrollTooltip && (
        <button className="scroll-top-button show" onClick={scrollToTop}>
          ⬆️
        </button>
      )}

      <div className="report-container">
        <h2>Book Reports Overview</h2>

        <div className="date-range-container">
          <h4>Select Date Range:</h4>
          <DatePicker.RangePicker
            value={dateRange}
            onChange={handleDateChange}
            format="MM-DD-YYYY"
            allowClear
            style={{
              padding: "10px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
              fontSize: "16px",
            }}
            dropdownStyle={{ padding: "10px" }}
          />
        </div>

        <div className="button-group">
          {[
            { id: "overallBooks", label: "📚 Overall Book Statistics" },
            { id: "bookCondition", label: "📊 Book Condition Breakdown" },
            { id: "booksAdded", label: "📈 Books Added Over Time" },
            { id: "mostBorrowed", label: "🔥 Top 10 Most Borrowed Books" },
            { id: "lateReturns", label: "⏳ Most Late Returns (Dept.)" },
            { id: "earlyReturns", label: "⏰ Most Early Returns (Dept.)" },
            {
              id: "returnedBooksTrend",
              label: "📉 Borrowed vs. Returned Books",
            },
            { id: "activeBorrowers", label: "🏆 Most Active Borrowers" },
            {
              id: "lateReturnedBooks",
              label: "🔄 Most Frequently Late-Returned Books",
            },
            { id: "peakBorrowingHours", label: "🕒 Peak Borrowing Hours" },
            {
              id: "mostPopularCategories",
              label: "📂 Most Popular Book Categories",
            },
            {
              id: "borrowedByYearLevel",
              label: "🎓 Books Borrowed by Year Level",
            },
            {
              id: "topReaders",
              label: "📖 Top 10 Readers",
            },
            {
              id: "topReadBooks",
              label: "📚 Top 10 Most Read Books",
            },
            {
              id: "borrowedFrequency",
              label: "📊 Borrowed Frequency (Day/Month)",
            },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToChart(item.id)}
              className="styled-button"
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="chart-wrapper">
          <div
            id="overallBooks"
            className={`chart-container ${
              activeChart === "overallBooks" ? "highlight" : ""
            }`}
          >
            <h3>Overall Book Statistics</h3>
            <ReactApexChart
              options={overallBooksData.options}
              series={overallBooksData.series}
              type="bar"
              height={350}
            />
          </div>
          <div
            id="bookCondition"
            className={`chart-container ${
              activeChart === "bookCondition" ? "highlight" : ""
            }`}
          >
            <h3>Book Condition Breakdown</h3>
            <ReactApexChart
              options={statusData.options}
              series={statusData.series}
              type="radialBar"
              height={350}
            />
          </div>
          <div
            id="booksAdded"
            className={`chart-container ${
              activeChart === "booksAdded" ? "highlight" : ""
            }`}
          >
            <h3>Monthly Book Acquisition</h3>
            <ReactApexChart
              options={addedBooksData.options}
              series={addedBooksData.series}
              type="bar"
              height={350}
            />
          </div>

          <div
            id="mostBorrowed"
            className={`chart-container ${
              activeChart === "mostBorrowed" ? "highlight" : ""
            }`}
          >
            <h3>Top 10 Faculty Most Borrowed Books</h3>
            <ReactApexChart
              options={mostBorrowedBooksData.options}
              series={mostBorrowedBooksData.series}
              type="bar"
              height={400}
            />
          </div>
          <div
            id="lateReturns"
            className={`chart-container ${
              activeChart === "lateReturns" ? "highlight" : ""
            }`}
          >
            <h3>Department with Most Late Returns</h3>
            <ReactApexChart
              options={lateReturnLevelsData.options}
              series={lateReturnLevelsData.series}
              type="pie"
              height={350}
            />
          </div>
          <div
            id="earlyReturns"
            className={`chart-container ${
              activeChart === "earlyReturns" ? "highlight" : ""
            }`}
          >
            <h3>Department with Most Early Returns</h3>
            <ReactApexChart
              options={earlyReturnLevelsData.options}
              series={earlyReturnLevelsData.series}
              type="pie"
              height={350}
            />
          </div>
          <div
            id="returnedBooksTrend"
            className={`chart-container ${
              activeChart === "returnedBooksTrend" ? "highlight" : ""
            }`}
          >
            <h3>Borrowed vs. Returned Books Trend</h3>
            <ReactApexChart
              options={borrowReturnTrendData.options}
              series={borrowReturnTrendData.series}
              type="area"
              height={350}
            />
          </div>
          <div
            id="activeBorrowers"
            className={`chart-container ${
              activeChart === "activeBorrowers" ? "highlight" : ""
            }`}
          >
            <h3>Top 10 Faculty Most Active Borrowers</h3>
            <ReactApexChart
              options={activeBorrowersData.options}
              series={activeBorrowersData.series}
              type="bar"
              height={350}
            />
          </div>
          <div
            id="lateReturnedBooks"
            className={`chart-container ${
              activeChart === "lateReturnedBooks" ? "highlight" : ""
            }`}
          >
            <h3>Most Frequently Late-Returned Books</h3>
            <ReactApexChart
              options={lateReturnBooksData.options}
              series={lateReturnBooksData.series}
              type="donut"
              height={350}
            />
          </div>
          <div
            id="peakBorrowingHours"
            className={`chart-container ${
              activeChart === "peakBorrowingHours" ? "highlight" : ""
            }`}
          >
            <h3>Peak Borrowing Hours</h3>
            <ReactApexChart
              options={peakHoursData.options}
              series={peakHoursData.series}
              type="bar"
              height={350}
            />
          </div>
          <div
            id="mostPopularCategories"
            className={`chart-container ${
              activeChart === "mostPopularCategories" ? "highlight" : ""
            }`}
          >
            <h3>Most Popular Book Categories</h3>
            <ReactApexChart
              options={categoryPopularityData.options}
              series={categoryPopularityData.series}
              type="pie"
              height={350}
            />
          </div>
          <div
            id="topReaders"
            className={`chart-container ${
              activeChart === "topReaders" ? "highlight" : ""
            }`}
          >
            <h3>Top 10 Students Who Read the Most</h3>
            <ReactApexChart
              options={topReadersData.options}
              series={topReadersData.series}
              type="bar"
              height={350}
            />
          </div>
          <div
            id="topReadBooks"
            className={`chart-container ${
              activeChart === "topReadBooks" ? "highlight" : ""
            }`}
          >
            <h3>Top 10 Most Read Books of Student</h3>
            <ReactApexChart
              options={topReadBooksData.options}
              series={topReadBooksData.series}
              type="bar"
              height={350}
            />
          </div>
          {/* <div
            id="borrowedByDay"
            className={`chart-container ${
              activeChart === "borrowedByDay" ? "highlight" : ""
            }`}
          >
            <h3>Books Borrowed by Day of the Week</h3>
            <ReactApexChart
              options={borrowedByDayOrMonthData.options}
              series={borrowedByDayOrMonthData.series}
              type="bar"
              height={350}
            />
          </div> */}

          <div
            id="borrowedFrequency"
            className={`chart-container ${
              activeChart === "borrowedFrequency" ? "highlight" : ""
            }`}
          >
            <div className="toggle-view">
              <button
                onClick={() => setBorrowedView("day")}
                className={borrowedView === "day" ? "active" : ""}
              >
                📅 By Day
              </button>
              <button
                onClick={() => setBorrowedView("month")}
                className={borrowedView === "month" ? "active" : ""}
              >
                📆 By Month
              </button>
            </div>
            <h3>
              Books Borrowed by{" "}
              {borrowedView === "day" ? "Day of the Week" : "Month"}
            </h3>
            <ReactApexChart
              options={borrowedFrequencyData.options}
              series={borrowedFrequencyData.series}
              type="bar"
              height={350}
            />
          </div>

          <div
            id="borrowedByYearLevel"
            className={`chart-container department ${
              activeChart === "borrowedByYearLevel" ? "highlight" : ""
            }`}
          >
            <h3>Books Borrowed by Year Level</h3>
            <ReactApexChart
              options={borrowByYearLevelData.options}
              series={borrowByYearLevelData.series}
              type="bar"
              height={350}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default BookReport;
